/**
 * Reconcilia cursor de replay com fila, sessão e generation ativa.
 */

import type { QueueItem } from '../queue/types';
import {
  getLastAckedSequenceSync,
  getLastReplayedSequenceSync,
} from './persistentReplay';
import { getActiveReplaySession } from './replaySession';
import { getQueuePartition } from '../queue/partitioning';

export type ReplayReconcileAction = 'ok' | 'recovery' | 'skip';

export interface ReplayReconcileResult {
  ok: boolean;
  action?: ReplayReconcileAction;
  reason?: string;
}

export function reconcileReplayCursor(
  queue: QueueItem[],
  sessionId: string
): ReplayReconcileResult {
  const lastAcked = getLastAckedSequenceSync(sessionId);
  const lastReplayed = getLastReplayedSequenceSync(sessionId);
  if (lastReplayed < lastAcked) {
    return { ok: false, action: 'recovery', reason: 'replayed_before_ack' };
  }

  const sessionItems = queue.filter(i => i.params.sessionId === sessionId);
  const maxSeq = sessionItems.reduce(
    (m, i) => Math.max(m, i.sessionSequence),
    0
  );
  if (lastReplayed > maxSeq + 50) {
    return { ok: false, action: 'recovery', reason: 'cursor_ahead_of_queue' };
  }

  const session = getActiveReplaySession();
  if (session) {
    const processing = sessionItems.filter(i => i.status === 'processing');
    if (
      processing.length > 0 &&
      session.originalEventIds.length > 0 &&
      !processing.some(i => session.originalEventIds.includes(i.id))
    ) {
      return { ok: false, action: 'recovery', reason: 'session_mismatch' };
    }
  }

  const ahead = sessionItems.filter(
    i =>
      i.sessionSequence <= lastAcked &&
      (i.status === 'queued' || i.status === 'failed')
  );
  if (ahead.length > 5) {
    return { ok: false, action: 'skip', reason: 'already_acked_in_queue' };
  }

  const partition = sessionItems[0]
    ? getQueuePartition(sessionItems[0])
    : undefined;
  if (partition && session?.originalEventIds.length) {
    void partition;
  }

  return { ok: true, action: 'ok' };
}

export function reconcileAllSessionCursors(
  queue: QueueItem[]
): ReplayReconcileResult[] {
  const sessions = new Set(queue.map(i => i.params.sessionId));
  const results: ReplayReconcileResult[] = [];
  for (const sid of sessions) {
    results.push(reconcileReplayCursor(queue, sid));
  }
  return results;
}
